import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Popover, popoverAnchorClass } from '../../primitives/Popover/Popover';
import { DropdownMenuItem } from '../DropdownMenuItem/DropdownMenuItem';
import styles from './Dropdown.module.css';

/**
 * Figma: `Dropdown` (984:36325) on the `Dropdown` page (984:36121).
 *
 * Variant properties are `Type=Button` and `Open=False|True`. `Open` is real
 * state, not a prop shape — it is the `open`/`defaultOpen` pair below. `Type`
 * has exactly one option today, so it becomes no prop at all; when the designer
 * adds a second (a text or avatar trigger, say) this is where the enum lands.
 *
 * The trigger is a `Buttons/Button` instance pinned to
 * `Hierarchy=Ghost Grey, State=Default, Icon only=True` with the `more` icon
 * trailing — 20x20, which the `Button` atom already renders — so it is reused
 * rather than restyled here.
 *
 * Figma positions the `Menu` frame ABSOLUTE at x=-100, y=24 against the 20x20
 * trigger, with `constraints.horizontal = MAX`. Against a 120-wide panel that
 * puts its right edge flush with the trigger's (-100 + 120 = 20) and its top 4px
 * below it. That is `Popover`'s `bottom-end`, which clears the trigger by
 * `space/6` rather than `space/4` — see the measurements in Popover.module.css;
 * the file is consistent that a panel dropping *below* its anchor clears it by 6,
 * and this node is the outlier at 4.
 */
export type DropdownItem = {
  /** Stable key, and what `onSelect` reports. */
  id: string;
  /** Figma `Text`. */
  label: string;
  /** Figma `🔀 Icon swap` on the row. */
  icon?: ReactNode;
  /** Figma `State=Disabled` on the row. */
  disabled?: boolean;
};

export type DropdownProps = {
  /**
   * The menu rows. Data-driven rather than hardcoded, so adding a command is a
   * fixture edit and not a component edit.
   */
  items: DropdownItem[];
  onSelect?: (id: string) => void;
  /** Controlled open state. Omit to let the component own it. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * The trigger's icon. Figma pins `more` (the horizontal ellipsis); override it
   * for a trigger that means something else.
   */
  icon?: ReactNode;
  /**
   * A **labelled** trigger instead of the icon-only one — Figma's `Tune
   * sentiment` modal (2037:7616) overrides the instance's `Buttons/Button` to
   * `Hierarchy=Tertiary, Icon only=False` with the text `View: All` and a
   * trailing `chevron down`. The component's own trigger is unchanged; this is
   * that override, as a prop.
   *
   * A labelled trigger also opens its menu from its **start** edge rather than
   * its end. The file only positions the menu for the 20px icon trigger, where
   * `bottom-end` keeps the 120-wide panel over it; hanging the same panel off
   * the end of an 89-wide button that sits at a table's left edge would push it
   * 31px past the table. Not in Figma — flagged in plan.md.
   */
  label?: string;
  /**
   * Accessible name for the menu, and for the trigger when it is icon-only.
   * Required — an icon-only trigger has no text to name it, and the menu always
   * needs a name. With `label` set, the trigger's visible text names it instead.
   * Not in Figma, which has no concept of an accessible name.
   */
  'aria-label': string;
  /**
   * The labelled trigger's own hierarchy. `tertiary` is Figma's in the `Tune
   * sentiment` modal (2037:7616), which is where the labelled trigger came
   * from; `Share report` (2214:97404) draws the same control `Ghost Color` for
   * the per-person access level, where it sits inside a form group rather than
   * on a table's filter bar.
   *
   * Ignored by the icon-only trigger, which Figma pins to `Ghost Grey`.
   */
  hierarchy?: 'tertiary' | 'ghostColor';
  disabled?: boolean;
  className?: string;
};

export function Dropdown({
  items,
  onSelect,
  open,
  defaultOpen = false,
  onOpenChange,
  icon,
  label,
  'aria-label': ariaLabel,
  hierarchy = 'tertiary',
  disabled = false,
  className,
}: DropdownProps) {
  const labelled = label !== undefined;
  const menuId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = (open ?? uncontrolledOpen) && !disabled;

  const setOpen = useCallback(
    (next: boolean) => {
      if (open === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [open, onOpenChange],
  );

  /*
   * Not in Figma. `role="menu"` promises arrow-key navigation, so the rows run a
   * roving tabindex: the menu is a single tab stop and Up/Down/Home/End move
   * focus within it. Disabled rows are skipped rather than focused-and-inert,
   * which is what a menu (as opposed to a listbox) should do.
   */
  const rowsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const enabledIndexes = items.reduce<number[]>((acc, item, index) => {
    if (!item.disabled) acc.push(index);
    return acc;
  }, []);

  const focusRow = (index: number) => {
    setActiveIndex(index);
    rowsRef.current[index]?.focus();
  };

  const openWith = (index: number) => {
    setOpen(true);
    setActiveIndex(index);
    // The panel mounts this frame; focus it once it exists.
    requestAnimationFrame(() => rowsRef.current[index]?.focus());
  };

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [setOpen],
  );

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (enabledIndexes.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openWith(enabledIndexes[0]);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      openWith(enabledIndexes[enabledIndexes.length - 1]);
    }
  };

  const onMenuKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (enabledIndexes.length === 0) return;
    const position = enabledIndexes.indexOf(activeIndex);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusRow(enabledIndexes[(position + 1) % enabledIndexes.length]);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusRow(enabledIndexes[(position - 1 + enabledIndexes.length) % enabledIndexes.length]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusRow(enabledIndexes[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusRow(enabledIndexes[enabledIndexes.length - 1]);
    } else if (event.key === 'Tab') {
      // A menu is not a tab stop sequence — leaving it closes it.
      close(false);
    }
  };

  /*
   * Popover closes on Escape, but focus would be left on a row that is being
   * unmounted. Catching Escape here first puts it back on the trigger, which is
   * where a keyboard user expects to land.
   */
  const onMenuKeyDownCapture = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape') close(true);
  };

  const handleSelect = (item: DropdownItem) => {
    onSelect?.(item.id);
    close(true);
  };

  return (
    <div className={[styles.root, popoverAnchorClass, className].filter(Boolean).join(' ')}>
      <Button
        ref={triggerRef}
        hierarchy={labelled ? hierarchy : 'ghostGrey'}
        iconTrailing={icon ?? <Icon name={labelled ? 'chevronDown' : 'more'} />}
        aria-label={labelled ? undefined : ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        disabled={disabled}
        onClick={() => setOpen(!isOpen)}
        onKeyDown={onTriggerKeyDown}
      >
        {label}
      </Button>

      {/*
       * `surface={false}`: Figma's `Menu` frame carries its own fill, stroke,
       * radius and shadow/lg, so letting Popover paint its surface too would
       * double the drop shadow. The stroke also differs from every other panel
       * in the file — see Dropdown.module.css.
       */}
      <Popover
        open={isOpen}
        onClose={() => close(false)}
        placement={labelled ? 'bottom-start' : 'bottom-end'}
        surface={false}
        motion="scale"
        role="menu"
        label={ariaLabel}
        id={menuId}
        className={styles.menu}
      >
        {/*
         * `role="none"` on the list: the panel above is the menu, and a `<ul>`
         * inside a `role="menu"` would otherwise announce a second, nested list
         * between the menu and its items.
         */}
        <ul
          role="none"
          className={styles.list}
          onKeyDown={onMenuKeyDown}
          onKeyDownCapture={onMenuKeyDownCapture}
        >
          {items.map((item, index) => (
            /* Figma `Menu items` (984:36350) — one 4px-padded wrapper per row. */
            <li key={item.id} role="none" className={styles.menuItems}>
              <DropdownMenuItem
                ref={(node: HTMLButtonElement | null) => {
                  rowsRef.current[index] = node;
                }}
                label={item.label}
                icon={item.icon}
                disabled={item.disabled}
                onSelect={() => handleSelect(item)}
                tabIndex={index === activeIndex ? 0 : -1}
              />
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}
